import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";

// Define form steps
type AuthStep = "email" | "password" | "signup";

export function SimpleAuthForm() {
  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<AuthStep>("email");
  
  // Auth context
  const { checkEmailExists, signInWithPassword, signUpWithEmail, loading } = useAuth();

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!email || !email.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    try {
      // Check if email exists
      const { exists, error } = await checkEmailExists(email);
      
      if (error) {
        // If we get a "No account found" error, move to signup
        if (error.includes("No account found")) {
          setStep("signup");
        } else {
          // For other errors, show the error message
          toast.error(error);
        }
      } else if (exists) {
        // Email exists, move to password form
        setStep("password");
      } else {
        // Email doesn't exist, move to signup
        setStep("signup");
      }
    } catch (error: any) {
      toast.error("Failed to check email. Please try again.");
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    const { success, error } = await signInWithPassword(email, password);
    
    if (success) {
      toast.success("Signed in successfully!");
    } else if (error) {
      toast.error(error);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    const { success, error } = await signUpWithEmail(email, password);
    
    if (success) {
      toast.success("Account created successfully!");
    } else if (error) {
      toast.error(error);
    }
  };

  const goBackToEmail = () => {
    setPassword("");
    setStep("email");
  };

  // Email form
  if (step === "email") {
    return (
      <form onSubmit={handleEmailSubmit} className="space-y-4">
        <div className="space-y-2">
          <Input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-gray-900 border-gray-700 text-[#FFFCF3]"
            required
            autoFocus
          />
          <p className="text-xs text-gray-400">
            Enter your email to sign in or create an account
          </p>
        </div>

        <Button
          type="submit"
          className="w-full bg-[#089BDF] hover:bg-[#089BDF]/90"
          disabled={loading}
        >
          {loading ? "Processing..." : "Continue"}
        </Button>
      </form>
    );
  }

  // Password form (for existing users)
  if (step === "password") {
    return (
      <form onSubmit={handleSignIn} className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center mb-4">
            <Button 
              type="button" 
              variant="ghost" 
              size="sm" 
              onClick={goBackToEmail}
              className="text-gray-400 hover:text-white"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </div>
          
          <p className="text-sm text-gray-400 mb-2">
            Sign in to your account
          </p>
          
          <Input
            type="email"
            value={email}
            disabled
            className="bg-gray-800 border-gray-700 text-gray-400 mb-3"
          />
          
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-gray-900 border-gray-700 text-[#FFFCF3]"
            required
            autoFocus
          />
          
          <p className="text-xs text-gray-400 mt-1">
            For demo purposes, use any password with at least 6 characters
          </p>
        </div>

        <Button
          type="submit"
          className="w-full bg-[#089BDF] hover:bg-[#089BDF]/90"
          disabled={loading}
        >
          {loading ? "Signing in..." : "Sign In"}
        </Button>
      </form>
    );
  }

  // Signup form (for new users)
  return (
    <form onSubmit={handleSignUp} className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center mb-4">
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={goBackToEmail}
            className="text-gray-400 hover:text-white"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </div>
        
        <p className="text-sm text-gray-400 mb-2">
          Create a new account
        </p>
        
        <Input
          type="email"
          value={email}
          disabled
          className="bg-gray-800 border-gray-700 text-gray-400 mb-3"
        />
        
        <Input
          type="password"
          placeholder="Create password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="bg-gray-900 border-gray-700 text-[#FFFCF3]"
          required
          autoFocus
        />
        
        <p className="text-xs text-gray-400 mt-1">
          Password must be at least 6 characters
        </p>
      </div>

      <Button
        type="submit"
        className="w-full bg-[#089BDF] hover:bg-[#089BDF]/90"
        disabled={loading}
      >
        {loading ? "Creating account..." : "Create Account"}
      </Button>
    </form>
  );
}
